import type { ReactNode } from 'react'

import type { FactConfidence } from '@/api/types'
import { CONFIDENCE_LABELS, factKindLabel, formatFactPeriod } from '@/lib/insights/factFormat'

import './facts.css'

/**
 * One fact as a list row, the same wherever a fact is shown (a digest's
 * drawer, an alert's figures): what it measures and of what, its value and
 * what it is against, its period and confidence. Each caller supplies the
 * value already written — the API's rendered text, or a stored value formatted
 * — and anything extra (an estimate's badge and workings, a source link).
 */
export function FactRow({
  kind,
  name,
  basis,
  value,
  comparison,
  periodStart,
  periodEnd,
  confidence,
  badge,
  children,
}: {
  kind: string
  /** The entity's name; null when it has none. */
  name: string | null
  basis: 'measured' | 'estimate'
  value: string
  /** What the value is against, written out; null without a comparison. */
  comparison: string | null
  periodStart: string
  periodEnd: string
  confidence: FactConfidence
  badge?: ReactNode
  children?: ReactNode
}) {
  return (
    <li className="ci-fact" data-basis={basis}>
      <div className="ci-fact__header">
        <span className="ci-fact__name">
          {factKindLabel(kind)}
          {name ? <span className="ci-facts__muted"> · {name}</span> : null}
        </span>
        {badge}
      </div>
      <p className="ci-fact__value">
        {value}
        {comparison ? <span className="ci-facts__muted"> against {comparison}</span> : null}
      </p>
      <p className="ci-fact__meta ci-facts__muted">
        {formatFactPeriod(periodStart, periodEnd)} · {CONFIDENCE_LABELS[confidence]}
      </p>
      {children}
    </li>
  )
}
